import { useEffect, useRef, useState } from "react";

/*
  useMinLoading

  Purpose:
  - Prevents "No data found" from flashing before API completes
  - Keeps loader visible for at least `minMs`
  - Allows empty state ONLY after first fetch finishes

  Params:
  - isFetching  → your real redux/local loading flag
  - minMs       → minimum loader time (default 1500ms)

  Returns:
  - loading     → controlled loading flag (use this in UI)
  - hasFetched  → true after first fetch completes
*/

export const useMinLoading = (isFetching, minMs = 300) => {
  const [loading, setLoading] = useState(Boolean(isFetching));
  const [hasFetched, setHasFetched] = useState(false);

  const fetchStartTime = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    // When fetch starts
    if (isFetching) {
      fetchStartTime.current = Date.now();
      setLoading(true);
      return;
    }

    // When fetch finishes
    if (fetchStartTime.current !== null) {
      const elapsed = Date.now() - fetchStartTime.current;
      const remaining = Math.max(0, minMs - elapsed);

      timerRef.current = setTimeout(() => {
        setLoading(false);
        setHasFetched(true);
      }, remaining);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isFetching, minMs]);

  return { loading, hasFetched };
};
